// AVRASTRO OS backup & restore.
//
// Backup file (formatVersion 1):
//   { format:'vero-os-backup', formatVersion, appVersion, schemaVersion, createdAt, workspace,
//     manifest:{ tables:{ <table>:{count, sha256} }, total }, checksum, data }
// Encrypted file: { format, formatVersion, encrypted:true, kdf:{PBKDF2,SHA-256,600000,salt}, cipher:{AES-GCM,iv}, ciphertext }
//
// Restore pipeline — never overwrites working data on parse:
//   parse → (decrypt) → validate checksums + schema → migrate in memory → diff/preview
//   → safety snapshot → transactional replace → verify (re-read + checksum) → auto-rollback on mismatch
import { TABLE_NAMES, validateDataset, SCHEMA_VERSION, emptyDataset, isLocalTable, type Dataset, type TableName, type ValidationIssue } from '@vero/domain';
import { migrateDataset, normalizeRow, toBase64, fromBase64, type FileMeta, type SnapshotMeta, type Workspace } from '@vero/store';
import { analyzeLegacy, b64decode, b64encode, type LegacyAnalysis } from '@vero/legacy-import';

export const BACKUP_FORMAT = 'vero-os-backup';
export const BACKUP_FORMAT_VERSION = 1;
export const APP_VERSION = '0.7.1';
export const KDF_ITERATIONS = 600_000;

export interface BackupManifest { tables: Record<string, { count: number; sha256: string }>; total: number }
export interface BackupFile {
  format: typeof BACKUP_FORMAT;
  formatVersion: number;
  appVersion: string;
  schemaVersion: number;
  createdAt: string;
  workspace: { id?: string; name?: string; label?: string };
  manifest: BackupManifest;
  checksum: string;
  data: Dataset;
  /** stored document files (0.4+): content-addressed, verified by SHA-256 on restore; optional so older
   *  readers simply ignore them */
  files?: BackupFileEntry[];
}
export interface BackupFileEntry { meta: FileMeta; sha256: string; content: string }
export interface EncryptedBackupFile {
  format: typeof BACKUP_FORMAT; formatVersion: number; encrypted: true;
  kdf: { name: 'PBKDF2'; hash: 'SHA-256'; iterations: number; salt: string };
  cipher: { name: 'AES-GCM'; iv: string };
  ciphertext: string;
}

export class BackupError extends Error {
  code: 'invalid_json' | 'password_required' | 'bad_password' | 'unsupported' | 'checksum' | 'schema' | 'newer_schema' | 'verify_failed';
  details?: unknown;
  constructor(code: BackupError['code'], details?: unknown) { super(code); this.code = code; this.details = details; }
}

// --- canonical JSON + hashing -----------------------------------------------------------
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v ?? null);
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(',')}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`).join(',')}}`;
}
export async function sha256(text: string): Promise<string> {
  const buf = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
const sortedRows = (rows: readonly { id: string }[]) => [...rows].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

export async function manifestOf(ds: Dataset): Promise<{ manifest: BackupManifest; checksum: string }> {
  const tables: BackupManifest['tables'] = {};
  let total = 0;
  const parts: string[] = [];
  for (const t of TABLE_NAMES) {
    const rows = sortedRows(ds[t] ?? []);
    const json = canonicalJson(rows);
    tables[t] = { count: rows.length, sha256: await sha256(json) };
    total += rows.length;
    parts.push(`${t}:${tables[t].sha256}`);
  }
  return { manifest: { tables, total }, checksum: await sha256(parts.join('\n')) };
}

export async function createBackup(ds: Dataset, workspace: BackupFile['workspace'] = {}, now = new Date().toISOString()): Promise<BackupFile> {
  const { manifest, checksum } = await manifestOf(ds);
  const data = Object.fromEntries(TABLE_NAMES.map((t) => [t, sortedRows(ds[t] ?? [])])) as unknown as Dataset;
  return { format: BACKUP_FORMAT, formatVersion: BACKUP_FORMAT_VERSION, appVersion: APP_VERSION, schemaVersion: SCHEMA_VERSION, createdAt: now, workspace, manifest, checksum, data };
}
export const serializeBackup = (b: BackupFile) => JSON.stringify(b);

async function sha256Bytes(bytes: Uint8Array): Promise<string> {
  const buf = await globalThis.crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
/** A full backup: every record plus every stored document file (decrypted from the device store, so the
 *  backup itself must be protected — use the encrypted export for anything leaving the computer). */
export async function createFullBackup(ws: Workspace, workspace: BackupFile['workspace'] = {}, now = new Date().toISOString()): Promise<BackupFile> {
  const b = await createBackup(ws.data, workspace, now);
  const files: BackupFileEntry[] = [];
  for (const meta of await ws.adapter.listFiles()) {
    const f = await ws.adapter.getFile(meta.id);
    if (f) files.push({ meta: f.meta, sha256: await sha256Bytes(f.content), content: toBase64(f.content) });
  }
  return files.length ? { ...b, files } : b;
}
/** Restore document files from a backup: each file is verified against its SHA-256 before it is stored;
 *  files already present (same id) are left untouched. Returns what happened, never throws for one bad file. */
export async function restoreFiles(ws: Workspace, files: readonly BackupFileEntry[] = []): Promise<{ restored: number; present: number; corrupt: string[] }> {
  const have = new Set((await ws.adapter.listFiles()).map((m) => m.id));
  let restored = 0, present = 0;
  const corrupt: string[] = [];
  for (const f of files) {
    if (have.has(f.meta.id)) { present++; continue; }
    const bytes = fromBase64(f.content);
    if (await sha256Bytes(bytes) !== f.sha256) { corrupt.push(f.meta.id); continue; }
    await ws.adapter.putFile(f.meta, bytes);
    restored++;
  }
  return { restored, present, corrupt };
}

// --- encryption ------------------------------------------------------------------------------
async function key(password: string, salt: Uint8Array<ArrayBuffer>, iterations: number) {
  const s = globalThis.crypto.subtle;
  const mat = await s.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey']);
  return s.deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, mat, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
export async function encryptBackup(text: string, password: string, iterations = KDF_ITERATIONS): Promise<string> {
  if (password.length < 10) throw new Error('password too short');
  const salt = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ct = await globalThis.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await key(password, salt, iterations), new TextEncoder().encode(text));
  const file: EncryptedBackupFile = { format: BACKUP_FORMAT, formatVersion: BACKUP_FORMAT_VERSION, encrypted: true, kdf: { name: 'PBKDF2', hash: 'SHA-256', iterations, salt: b64encode(salt) }, cipher: { name: 'AES-GCM', iv: b64encode(iv) }, ciphertext: b64encode(ct) };
  return JSON.stringify(file);
}
async function decryptBackup(f: EncryptedBackupFile, password: string): Promise<string> {
  try {
    const k = await key(password, b64decode(f.kdf.salt), f.kdf.iterations);
    return new TextDecoder().decode(await globalThis.crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64decode(f.cipher.iv) }, k, b64decode(f.ciphertext)));
  } catch { throw new BackupError('bad_password'); }
}

// --- parse any source (AVRASTRO OS backup or legacy VÉRO) ----------------------------------------
export type ParsedSource =
  | { kind: 'vero-os'; file: BackupFile; dataset: Dataset; issues: ValidationIssue[] }
  | { kind: 'legacy'; analysis: LegacyAnalysis; dataset: Dataset; issues: ValidationIssue[] };

export async function parseSource(text: string, opts: { password?: string; candidateIndex?: number; now?: string } = {}): Promise<ParsedSource> {
  let parsed: unknown;
  try { parsed = JSON.parse(text.trim().replace(/^﻿/, '')); } catch { parsed = undefined; }
  const o = parsed as Record<string, unknown> | undefined;
  if (o && o.format === BACKUP_FORMAT) {
    let file = o as unknown as BackupFile;
    if ((o as unknown as EncryptedBackupFile).encrypted) {
      if (!opts.password) throw new BackupError('password_required');
      file = JSON.parse(await decryptBackup(o as unknown as EncryptedBackupFile, opts.password)) as BackupFile;
    }
    if (file.schemaVersion > SCHEMA_VERSION) throw new BackupError('newer_schema', file.schemaVersion);
    // integrity: recompute manifest and compare
    const { manifest, checksum } = await manifestOf(file.data);
    const bad = TABLE_NAMES.filter((t) => (file.manifest.tables[t]?.sha256 ?? manifest.tables[t]!.sha256) !== manifest.tables[t]!.sha256 || (file.manifest.tables[t]?.count ?? manifest.tables[t]!.count) !== manifest.tables[t]!.count);
    if (bad.length || checksum !== file.checksum) throw new BackupError('checksum', bad);
    const dataset = migrateDataset(file.data, file.schemaVersion);
    return { kind: 'vero-os', file, dataset, issues: validateDataset(dataset) };
  }
  try {
    const analysis = await analyzeLegacy(text, { password: opts.password, candidateIndex: opts.candidateIndex, importedAt: opts.now });
    return { kind: 'legacy', analysis, dataset: analysis.dataset, issues: validateDataset(analysis.dataset) };
  } catch (e) {
    const code = (e as { code?: string }).code;
    if (code === 'password_required' || code === 'bad_password' || code === 'invalid_json' || code === 'unsupported') throw new BackupError(code);
    throw e;
  }
}

// --- diff & merge ------------------------------------------------------------------------------
export interface TableDiff { added: number; removed: number; changed: number; unchanged: number }
export type DatasetDiff = Record<TableName, TableDiff>;

const rowEq = (a: unknown, b: unknown) => canonicalJson(a) === canonicalJson(b);

export function diffDatasets(current: Dataset, incoming: Dataset): DatasetDiff {
  const out = {} as DatasetDiff;
  for (const t of TABLE_NAMES) {
    const cur = new Map((current[t] ?? []).map((r) => [r.id, r]));
    const inc = new Map((incoming[t] ?? []).map((r) => [r.id, r]));
    const d: TableDiff = { added: 0, removed: 0, changed: 0, unchanged: 0 };
    for (const [id, r] of inc) { const c = cur.get(id); if (!c) d.added++; else if (rowEq(c, r)) d.unchanged++; else d.changed++; }
    for (const id of cur.keys()) if (!inc.has(id)) d.removed++;
    out[t] = d;
  }
  return out;
}

export interface MergeConflict { table: TableName; id: string; kept: 'local' | 'incoming'; reason: string }

/**
 * Merge incoming into current (upsert by id). A local row that was edited after the incoming
 * copy (higher version and newer updatedAt) is kept and reported — local work is never lost.
 * `audit_events` from the incoming side are appended, never overwritten.
 */
export function mergeDatasets(current: Dataset, incoming: Dataset): { dataset: Dataset; conflicts: MergeConflict[] } {
  const conflicts: MergeConflict[] = [];
  const out = emptyDataset() as Record<TableName, unknown[]>;
  for (const t of TABLE_NAMES) {
    const map = new Map<string, { id: string; updatedAt?: string; version?: number }>((current[t] ?? []).map((r) => [r.id, r as never]));
    for (const r of (incoming[t] ?? []) as { id: string; updatedAt?: string; version?: number }[]) {
      const local = map.get(r.id);
      if (local && !rowEq(local, r) && (local.version ?? 1) > 1 && String(local.updatedAt ?? '') > String(r.updatedAt ?? '')) {
        conflicts.push({ table: t, id: r.id, kept: 'local', reason: 'local copy edited after the imported version' });
        continue;
      }
      map.set(r.id, r);
    }
    out[t] = [...map.values()];
  }
  return { dataset: out as unknown as Dataset, conflicts };
}

// --- restore pipeline ----------------------------------------------------------------------------
export interface RestorePreview {
  mode: 'replace' | 'merge';
  target: Dataset;
  diff: DatasetDiff;
  conflicts: MergeConflict[];
  issues: ValidationIssue[];
  totals: { before: number; after: number };
}

export function previewRestore(current: Dataset, incoming: Dataset, mode: 'replace' | 'merge'): RestorePreview {
  // rows are stored without null/undefined fields — normalise first so checksums compare exactly.
  // Device-local tables (the sync outbox and conflict inbox) always stay as they are on THIS device: a backup from
  // another device must never replace this device's pending changes.
  const clean = Object.fromEntries(TABLE_NAMES.map((t) => [t, isLocalTable(t) ? (current[t] ?? []) : (incoming[t] ?? []).map((r) => normalizeRow(r))])) as unknown as Dataset;
  const { dataset: merged, conflicts } = mode === 'merge' ? mergeDatasets(current, clean) : { dataset: clean, conflicts: [] };
  const target = bumpChangedVersions(current, merged);
  const count = (d: Dataset) => TABLE_NAMES.reduce((s, t) => s + (d[t]?.length ?? 0), 0);
  return { mode, target, diff: diffDatasets(current, target), conflicts, issues: validateDataset(target), totals: { before: count(current), after: count(target) } };
}

/**
 * A row that already exists here and changes through a restore/import becomes a NEW version (higher than both the local
 * and the imported one). The sync protocol can then send it as an ordinary change of the version this device last saw
 * — never a silent overwrite — and the server detects real conflicts. Rows that are new or unchanged keep their version.
 */
function bumpChangedVersions(current: Dataset, target: Dataset): Dataset {
  const out = {} as Record<TableName, unknown[]>;
  for (const t of TABLE_NAMES) {
    const rows = (target[t] ?? []) as unknown as Record<string, unknown>[];
    if (isLocalTable(t)) { out[t] = rows; continue; }
    const before = new Map(((current[t] ?? []) as unknown as Record<string, unknown>[]).map((r) => [r.id as string, r]));
    out[t] = rows.map((r) => {
      const b = before.get(r.id as string);
      if (!b || rowEq(b, r)) return r;
      return { ...r, version: Math.max(Number(b.version ?? 1), Number(r.version ?? 1)) + 1 };
    });
  }
  return out as unknown as Dataset;
}

export interface RestoreResult { snapshot: SnapshotMeta; verified: true; records: number; checksum: string }

export async function takeSnapshot(ws: Workspace, label: string, reason: SnapshotMeta['reason'], now = new Date().toISOString()): Promise<SnapshotMeta> {
  const file = await createBackup(ws.data, { label }, now);
  const text = serializeBackup(file);
  const meta: SnapshotMeta = { id: `snap_${now.replace(/[^0-9]/g, '')}_${Math.random().toString(36).slice(2, 7)}`, label, reason, createdAt: now, records: file.manifest.total, bytes: text.length, checksum: file.checksum };
  await ws.adapter.saveSnapshot(meta, text);
  return meta;
}

/**
 * Automatic recovery snapshot (0.6.1): at most one every `minHours`, only when the data changed since the newest
 * snapshot (checksum), keeping the newest `keep` automatic ones. Snapshots taken for other reasons (before an
 * import, a restore, a merge…) are never pruned here. Returns the new snapshot, or null when nothing was needed.
 */
export async function autoSnapshot(ws: Workspace, opts: { keep?: number; minHours?: number; label?: string; now?: string } = {}): Promise<SnapshotMeta | null> {
  const keep = Math.max(1, opts.keep ?? 7);
  const minHours = opts.minHours ?? 12;
  const now = opts.now ?? new Date().toISOString();
  const all = await ws.adapter.listSnapshots();
  const autos = all.filter((s) => s.reason === 'auto').sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const newest = [...all].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  const total = TABLE_NAMES.reduce((n, t) => n + (ws.data[t]?.length ?? 0), 0);
  let made: SnapshotMeta | null = null;
  const due = !autos[0] || Date.parse(now) - Date.parse(autos[0].createdAt) >= minHours * 3600_000;
  if (due && total > 0) {
    const { checksum } = await manifestOf(ws.data);
    if (!newest || newest.checksum !== checksum) made = await takeSnapshot(ws, opts.label ?? 'auto', 'auto', now);
  }
  const keepIds = new Set([...(made ? [made] : []), ...autos].slice(0, keep).map((s) => s.id));
  for (const s of autos) if (!keepIds.has(s.id)) await ws.adapter.deleteSnapshot(s.id);
  return made;
}

/** Commit a previewed restore/import transactionally, verify, and roll back automatically on mismatch. */
export async function commitRestore(ws: Workspace, preview: RestorePreview, opts: { label: string; reason?: SnapshotMeta['reason']; now?: string }): Promise<RestoreResult> {
  if (preview.issues.length) throw new BackupError('schema', preview.issues.slice(0, 20));
  const snapshot = await takeSnapshot(ws, opts.label, opts.reason ?? 'pre-restore', opts.now);
  // verification covers the business data; the device-local outbox may change meanwhile (a paired device queues the
  // restored rows for sync right after the replace)
  const businessOnly = (d: Dataset) => Object.fromEntries(TABLE_NAMES.map((t) => [t, isLocalTable(t) ? [] : (d[t] ?? [])])) as unknown as Dataset;
  const expected = await manifestOf(businessOnly(preview.target));
  try {
    await ws.replaceAll(preview.target);
    const actual = await manifestOf(businessOnly(await ws.adapter.loadAll()));
    if (actual.checksum !== expected.checksum) throw new BackupError('verify_failed', { expected: expected.manifest, actual: actual.manifest });
    return { snapshot, verified: true, records: expected.manifest.total, checksum: expected.checksum };
  } catch (e) {
    // automatic rollback to the safety snapshot
    const text = await ws.adapter.readSnapshot(snapshot.id);
    if (text) { try { await ws.replaceAll((JSON.parse(text) as BackupFile).data); } catch (re) { console.error('[restore] rollback failed', re); } }
    throw e instanceof BackupError ? e : new BackupError('verify_failed', String((e as Error)?.message ?? e));
  }
}

export async function rollbackToSnapshot(ws: Workspace, snapshotId: string, now?: string): Promise<RestoreResult> {
  const text = await ws.adapter.readSnapshot(snapshotId);
  if (!text) throw new BackupError('unsupported');
  const parsed = await parseSource(text);
  return commitRestore(ws, previewRestore(ws.data, parsed.dataset, 'replace'), { label: `before rollback to ${snapshotId}`, reason: 'pre-rollback', now });
}
